/**
 * @fileoverview Footnotes: [vdx-footnotes-article] and [vdx-footnotes-footnotes] content with [1], [2] notation is turned into links; optional hover tooltips when vdx-footnotes-article="hover".
 */

const MODULE = 'footnotes';

const SELECTOR_ARTICLE = '[vdx-footnotes-article]';
const SELECTOR_FOOTNOTES = '[vdx-footnotes-footnotes]';
const SELECTOR_LINK = '[vdx-footnotes-link]';
const ATTR_ARTICLE = 'vdx-footnotes-article';
const ID_PREFIX_ARTICLE = 'vdx-fn-article-';
const ID_PREFIX_FOOTNOTE = 'vdx-fn-footnote-';
const CLASS_ARTICLE_LINK = 'vdx--fn-article-link';
const CLASS_FOOTNOTE_LINK = 'vdx--fn-footnote-link';
const CLASS_TOOLTIP = 'vdx--fn-footnote';

/** Matches footnote notation e.g. [1], [2], [12]. */
const REGEX_FOOTNOTE = /\[\d+\]/g;

/**
 * Extracts the number from a footnote match (e.g. "[1]" → "1").
 * @param {string} match
 * @returns {string}
 */
function getFootnoteNumber(match) {
    return String(match).replace(/\[|\]/g, '');
}

/**
 * Replaces one footnote notation in html with the given link HTML. Replaces first occurrence only.
 * @param {string} html
 * @param {string} notation - e.g. "[1]"
 * @param {string} linkHtml
 * @returns {string}
 */
function replaceFirstFootnote(html, notation, linkHtml) {
    const idx = html.indexOf(notation);
    if (idx === -1) return html;
    return html.slice(0, idx) + linkHtml + html.slice(idx + notation.length);
}

/**
 * Creates a link element for article-side footnote (links to footnote block).
 * @param {string} notation - e.g. "[1]"
 * @param {string} number - e.g. "1"
 * @returns {HTMLAnchorElement}
 */
function createArticleLink(notation, number) {
    const a = document.createElement('a');
    a.href = `#${ID_PREFIX_FOOTNOTE}${number}`;
    a.id = `${ID_PREFIX_ARTICLE}${number}`;
    a.setAttribute('vdx-footnotes-link', '');
    a.classList.add(CLASS_ARTICLE_LINK);
    a.textContent = notation;
    return a;
}

/**
 * Creates a link element for footnote-block side (links back to article).
 * @param {string} notation - e.g. "[1]"
 * @param {string} number - e.g. "1"
 * @returns {HTMLAnchorElement}
 */
function createFootnoteLink(notation, number) {
    const a = document.createElement('a');
    a.href = `#${ID_PREFIX_ARTICLE}${number}`;
    a.id = `${ID_PREFIX_FOOTNOTE}${number}`;
    a.classList.add(CLASS_FOOTNOTE_LINK);
    a.textContent = notation;
    return a;
}

/**
 * Replaces all [n] footnote notations in the article element with links to the footnote block.
 * @param {Element} articleEl
 */
function replaceArticleNotation(articleEl) {
    const text = articleEl.textContent != null ? articleEl.textContent : '';
    const matches = text.match(REGEX_FOOTNOTE);
    if (!matches || !matches.length) return;
    let html = articleEl.innerHTML;
    matches.forEach((notation) => {
        const number = getFootnoteNumber(notation);
        const link = createArticleLink(notation, number);
        html = replaceFirstFootnote(html, notation, link.outerHTML);
    });
    articleEl.innerHTML = html;
}

/**
 * Replaces all [n] footnote notations in the footnotes block with links back to the article.
 * @param {Element} footnotesEl
 */
function replaceFootnoteBlockNotation(footnotesEl) {
    const text = footnotesEl.textContent != null ? footnotesEl.textContent : '';
    const matches = text.match(REGEX_FOOTNOTE);
    if (!matches || !matches.length) return;
    let html = footnotesEl.innerHTML;
    matches.forEach((notation) => {
        const number = getFootnoteNumber(notation);
        const link = createFootnoteLink(notation, number);
        html = replaceFirstFootnote(html, notation, link.outerHTML);
    });
    footnotesEl.innerHTML = html;
}

/**
 * Returns the tooltip text for a footnote (next sibling text of the footnote target element).
 * @param {string} number
 * @returns {string}
 */
function getFootnoteTooltipText(number) {
    const el = document.querySelector(`#${ID_PREFIX_FOOTNOTE}${number}`);
    const next = el && el.nextSibling;
    return next && next.textContent != null ? String(next.textContent).trim() : '';
}

/**
 * Wires hover tooltips on [vdx-footnotes-link]: show tooltip on mouseenter, remove on mouseleave.
 * @param {Element} container - Optional container to scope query (default document).
 */
function wireHoverTooltips(container) {
    const root = container && container.querySelector ? container : document;
    const links = root.querySelectorAll(SELECTOR_LINK);
    links.forEach((link) => {
        let tooltipEl = null;
        const showTooltip = () => {
            if (tooltipEl) return;
            const number = getFootnoteNumber(link.textContent || '');
            const text = getFootnoteTooltipText(number);
            tooltipEl = document.createElement('div');
            tooltipEl.classList.add(CLASS_TOOLTIP);
            tooltipEl.textContent = text;
            link.appendChild(tooltipEl);
        };
        const hideTooltip = () => {
            if (tooltipEl) {
                tooltipEl.remove();
                tooltipEl = null;
            }
        };
        link.addEventListener('mouseenter', showTooltip);
        link.addEventListener('mouseleave', hideTooltip);
    });
}

/**
 * Initializes footnotes: replaces [n] in [vdx-footnotes-article] and [vdx-footnotes-footnotes] with links; if vdx-footnotes-article="hover", adds hover tooltips.
 */
export function generateFootnotes() {
    if (typeof document === 'undefined') return;
    const articles = document.querySelectorAll(SELECTOR_ARTICLE);
    const footnoteBlocks = document.querySelectorAll(SELECTOR_FOOTNOTES);
    if (!articles.length || !footnoteBlocks.length) return;

    articles.forEach(replaceArticleNotation);
    footnoteBlocks.forEach(replaceFootnoteBlockNotation);

    const firstArticle = articles[0];
    const hoverMode = firstArticle && firstArticle.getAttribute(ATTR_ARTICLE) === 'hover';
    if (hoverMode) wireHoverTooltips(document);
}
